import {
  CuriosityFilterByType,
  CuriosityNamespaceType,
  CuriosityValue,
  isCuriosityTranslation,
} from '@procureai/curiosity-ui-mui';
import { FormFieldWrapper, SingleColumnFormLayout } from '@procureai/ui-form-mui';
import { isNotNil } from 'ramda';
import { useMemo } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SupplierContactAutocomplete } from '../SupplierContactAutocomplete/SupplierContactAutocomplete';
import { SupplierAutocomplete } from '~/components/SupplierAutocomplete';
import { supplierAutocompleteHelper } from '~/components/SupplierAutocomplete/SupplierAutocomplete.helper';
import { SupplierLocationAutocomplete } from '~/components/SupplierLocationAutocomplete';
import { useFormFieldErrorTranslation } from '~/hooks';

import { commonInputProps } from './SupplierContactForm.common';
import { BatchSupplierContact, SupplierContact, supplierContactDataDefault } from './SupplierContactForm.schema';

export interface SupplierContactFormProps {
  viewOnly?: boolean;
  disableSupplierChange?: boolean;
  disabled?: boolean;
  suppliersOmit?: string[];
  // be aware that the used formSchema should have a nullable location field if you hide it
  // otherwise the form might have an error which can't be seen by the user
  hideLocation?: boolean;
  index?: number;
}

export const SupplierContactForm = ({
  disableSupplierChange,
  disabled,
  suppliersOmit,
  viewOnly,
  hideLocation,
  index,
}: SupplierContactFormProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SupplierContactForm' });
  const { errorToHelperText } = useFormFieldErrorTranslation();
  const { mapContact, mapLocation } = useMemo(() => supplierAutocompleteHelper(), []);

  const form = useFormContext<SupplierContact | BatchSupplierContact>();

  const watchSupplier = form.watch(isNotNil(index) ? `suppliers.${index}.supplier` : 'supplier');
  const supplierId = watchSupplier?.internalId;

  return (
    <>
      <SingleColumnFormLayout>
        {disableSupplierChange && viewOnly ? null : (
          <Controller
            name={isNotNil(index) ? `suppliers.${index}.supplier` : 'supplier'}
            control={form.control}
            render={({ field, fieldState: { error } }) => {
              const { internalId } = field.value || {};
              const nestedError: FieldError | undefined = (error as any)?.internalId;
              const disabledValue = disabled || field.disabled || disableSupplierChange;
              return (
                <FormFieldWrapper
                  label={t('contact.company.procureaiSupplierId')}
                  error={!!error}
                  disabled={disabledValue}
                  helperText={errorToHelperText(nestedError)}
                >
                  {viewOnly ? (
                    <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER} i18nKey={internalId} fallback="-" />
                  ) : (
                    <SupplierAutocomplete
                      {...field}
                      disabled={disabledValue}
                      value={internalId}
                      size={commonInputProps.size}
                      disableClearable={false}
                      filterOptionsByKey={suppliersOmit}
                      onChange={(value) => {
                        if (value && isCuriosityTranslation(value)) {
                          field.onChange({
                            internalId: value.key,
                            name: value.value,
                            externalReference: value.entity.sourceReference,
                            location: mapLocation(value.entity.locations),
                            contact: mapContact(value.entity.contacts),
                            isQuoted: false,
                          });
                        } else {
                          field.onChange({
                            location: null,
                            contact: null,
                          });
                        }
                      }}
                      TextFieldProps={{
                        ...commonInputProps,
                        error: !!error,
                      }}
                    />
                  )}
                </FormFieldWrapper>
              );
            }}
          />
        )}
        {hideLocation ? null : (
          <Controller
            // using a key to force re-render of the component when supplier changes
            key={`${supplierId}-location`}
            control={form.control}
            name={isNotNil(index) ? `suppliers.${index}.supplier.location` : 'supplier.location'}
            render={({ field, fieldState: { error } }) => {
              const { internalId } = field.value || {};
              const disabledValue = disabled || !supplierId;
              return (
                <FormFieldWrapper
                  label={t('contact.company.chooseAddress')}
                  error={!!error}
                  disabled={disabledValue}
                  helperText={errorToHelperText(error)}
                >
                  {viewOnly ? (
                    <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER_LOCATION} i18nKey={internalId!} fallback="-" />
                  ) : (
                    <SupplierLocationAutocomplete
                      {...field}
                      value={internalId || null}
                      multiple={false}
                      disableClearable={false}
                      disabled={disabledValue}
                      size={commonInputProps.size}
                      filter={{
                        by: CuriosityFilterByType.SUPPLIER_ID,
                        value: supplierId,
                      }}
                      onChange={(value) => {
                        if (value && isCuriosityTranslation(value)) {
                          field.onChange({
                            internalId: value.key,
                            countryCode: value.entity.countryCode,
                            city: value.entity.city,
                            address: value.entity.address,
                            postalCode: value.entity.postalCode,
                          });
                        } else {
                          field.onChange(null);
                        }
                      }}
                      TextFieldProps={{
                        ...commonInputProps,
                        error: !!error,
                      }}
                    />
                  )}
                </FormFieldWrapper>
              );
            }}
          />
        )}
        <Controller
          // using a key to force re-render of the component when supplier changes
          key={`${supplierId}-contact`}
          control={form.control}
          name={isNotNil(index) ? `suppliers.${index}.supplier.contact` : 'supplier.contact'}
          render={({ field, fieldState: { error } }) => {
            const { internalId } = field.value || {};
            const disabledValue = disabled || !supplierId;
            return (
              <FormFieldWrapper
                label={t('contact.company.chooseContact')}
                error={!!error}
                disabled={disabledValue}
                helperText={errorToHelperText(error)}
              >
                {viewOnly ? (
                  <CuriosityValue ns={CuriosityNamespaceType.SUPPLIER_CONTACT} i18nKey={internalId} fallback="-" />
                ) : (
                  <SupplierContactAutocomplete
                    {...field}
                    value={internalId || null}
                    multiple={false}
                    disabled={disabled || !supplierId}
                    size={commonInputProps.size}
                    filter={{
                      by: CuriosityFilterByType.SUPPLIER_ID,
                      value: supplierId,
                    }}
                    onChange={(value) => {
                      if (value) {
                        if (isCuriosityTranslation(value)) {
                          field.onChange({
                            internalId: value.key,
                            email: value.entity.email,
                            language: value.entity.preferredLanguage,
                            firstName: value.entity.firstName,
                            lastName: value.entity.lastName,
                          });
                        } else {
                          // sentinel provided
                          field.onChange({
                            ...supplierContactDataDefault.supplier?.contact,
                            internalId: value,
                          });
                        }
                      } else {
                        field.onChange(null);
                      }
                    }}
                    TextFieldProps={{
                      ...commonInputProps,
                      error: !!error,
                    }}
                  />
                )}
              </FormFieldWrapper>
            );
          }}
        />
      </SingleColumnFormLayout>
    </>
  );
};
